import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from '@ngx-translate/core';

import { Subscription } from 'rxjs';

import { TableConfig } from '@app/shared/util/table-config.utils';

import { NotificationService } from '@services/notification.service';

import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';

import { Log } from 'ng2-logger';
import { ToolRequest } from "@models/toolRequests.model";
import { ToolTransportService } from "@services/tool-transport.service";
import { AppUtils } from "@app/shared/util/app.utils";

@Component({
    selector: 'app-line-requests',
    templateUrl: './line-requests.component.html',
    styleUrls: ['./line-requests.component.scss']
})
export class LineRequestsComponent implements OnInit, OnDestroy {

    @ViewChild(ConfirmationModalComponent, {static: false}) confirmationModal: ConfirmationModalComponent;

    _logger = Log.create(LineRequestsComponent.name);

    toolReturns = [];
    tableConfig = new TableConfig();
    isLoading = false;
    hasConfirmationInProgress = false;

    newToolReturnsSubscription: Subscription;
    selectedPlantsChangedSubscription: Subscription;

    constructor(private toolTransportService: ToolTransportService,
                private notificationService: NotificationService,
                private toastr: ToastsManager,
                private translateService: TranslateService) {
    }

    ngOnInit() {
        this.hasConfirmationInProgress = false;
        this.getToolReturns(this.tableConfig);
        this.newToolReturnsSubscription = this.notificationService.milkrunReturnQueueAsObservable.subscribe(
            (data) => {
                this._logger.info('milkrunReturnQueue:: Socket refreshed tool returns, retrieved data', data);
                this.getToolReturns(this.tableConfig);
            }
        );

        this.toolTransportService.loadEventObservable.subscribe(
            () => {
                this.getToolReturns(this.tableConfig);
            }
        );

        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(data => {
            this.getToolReturns(this.tableConfig);
        });
    }

    getToolReturns(tableConfig = this.tableConfig) {
        this._logger.info('getToolReturns:: Retrieving tool returns');
        this.isLoading = true;
        tableConfig = AppUtils.appendPlantsQuery(tableConfig);
        this.toolTransportService.getLineRequests(tableConfig, 'LINE').subscribe(
            (data) => {
                this._logger.info('getToolReturns:: API Returned data', data);
                this.toolReturns = data['items'];
                this.toolReturns.forEach(function (toolReturn) {
                    toolReturn.atLine = true;
                })
                this.tableConfig.pager.count = data['total'];
                this.isLoading = false;
            },
            (error) => {
                this._logger.error('getToolReturns:: API Returned error', error);
                this.isLoading = false;
            }
        )
    }

    onPageChange(tableConfig: TableConfig) {
        this.getToolReturns(tableConfig);
    }

    onLoadToolReturn(toolReturn: any) {
        this.confirmationModal.open({
            toolReturn: toolReturn,
            action: 'load'
        });
    }

    onUnloadToolReturn(toolReturn: any) {
        this.confirmationModal.open({
            toolReturn: toolReturn,
            action: 'unload'
        });
    }

    onConfirmAction(event: any) {
        this.hasConfirmationInProgress = true;
        switch (event.action) {
            case 'load':
                this.confirmLoadToolReturn(event.toolReturn);
                break;
            case 'unload':
                this.confirmUnloadToolReturn(event.toolReturn);
                break;
        }
    }

    confirmLoadToolReturn(toolReturn: any) {
        this._logger.info("confirmLoadToolReturn:: Loading tool request for transport");
        this.toolTransportService.loadToolTransportForReturn(toolReturn.id).subscribe(
            (data) => {
                this.confirmationModal.close();
                this._logger.info('loadToolReturn:: API Returned data', data);
                this.toastr.success(this.buildLoadMessage(toolReturn.name));
                this.hasConfirmationInProgress = false;
            },
            (error) => {
                this.confirmationModal.close();
                this._logger.error('loadToolReturn:: API Returned error', error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    confirmUnloadToolReturn(toolRequest: ToolRequest) {
        this._logger.info("confirmUnloadToolReturn:: Unloading tool request at line");
        this.toolTransportService.unloadToolTransportForRequest(toolRequest.id).subscribe(
            (data) => {
                this.confirmationModal.close();
                this._logger.info('unloadToolReturn:: API Returned data', data);
                this.toastr.success(this.buildUnloadMessage(toolRequest.name));
                this.hasConfirmationInProgress = false;
            },
            (error) => {
                this.confirmationModal.close();
                this._logger.error('unloadToolReturn:: API Returned error', error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    buildLoadMessage(toolRequestName) {
        return this.translateService.instant('milkrun.transportation-requests.toasts.toolReturnLoaded',
            {toolRequestName: toolRequestName});
    }

    buildUnloadMessage(toolRequestName) {
        return this.translateService.instant('milkrun.transportation-requests.toasts.toolReturnUnloaded',
            {toolRequestName: toolRequestName});
    }

    ngOnDestroy() {
        if (this.newToolReturnsSubscription) {
            this.newToolReturnsSubscription.unsubscribe();
        }
    }

}
